import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Network,
  Database,
  Layers,
  ShieldCheck,
  Calendar,
  GitMerge,
  Globe,
  FileText,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ROIResultsCapture from '@/components/ROIResultsCapture';

const CALENDLY = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';

const EZCLOUD_TO_DYNAMICS = [
  { label: 'Vendor invoices (AP journal)', note: 'With dimensions: financial, department, project, cost center' },
  { label: '3-way match results', note: 'Against purchase orders and product receipts' },
  { label: 'Approval metadata', note: 'Compatible with workflow history on the vendor invoice' },
  { label: 'Linked invoice images', note: 'Attached to the vendor invoice record' },
  { label: 'Tax + withholding codes', note: 'Per legal entity and jurisdiction' },
];

const DYNAMICS_TO_EZCLOUD = [
  'Vendor master (all legal entities)',
  'Chart of accounts + financial dimensions',
  'Open purchase orders + product receipts',
  'Legal entity hierarchy + intercompany setup',
  'Projects + cost categories',
  'Tax codes, payment terms, banking',
];

const DAY_ONE = [
  {
    icon: GitMerge,
    title: 'Vendor invoice automation',
    body: "Capture, classify, code (including financial dimensions), 3-way match, and post to Dynamics 365 as a fully-coded vendor invoice — no keying in AP.",
  },
  {
    icon: Globe,
    title: 'Multi-legal-entity routing',
    body: "For D365 F&O customers with multiple legal entities, EZ Cloud routes invoices to the correct LE with intercompany flows built in.",
  },
  {
    icon: Layers,
    title: 'Full financial-dimensions coding',
    body: "All your Dynamics dimensions — department, cost center, project, business unit, custom — are available at capture and can be auto-coded from rules.",
  },
  {
    icon: ShieldCheck,
    title: 'Power Platform–friendly',
    body: 'EZ Cloud plays nicely with Power Automate workflows you already have. If you want AP events to trigger downstream Power Platform automations, the hooks are there.',
  },
];

const FAQS = [
  {
    q: 'Which Dynamics products does EZ Cloud integrate with?',
    a: 'Primary integrations: Microsoft Dynamics 365 Finance & Operations (F&O) and Dynamics 365 Business Central. Legacy AX 2012 is supported for customers mid-migration. Dynamics GP and SL are deprecated by Microsoft; contact sales if you\'re still running them.',
  },
  {
    q: 'Does EZ Cloud support Business Central (mid-market) or only F&O?',
    a: 'Both. Business Central integration uses BC REST APIs and fits mid-market operations well. F&O uses OData + Data Management Framework, tuned for enterprise multi-legal-entity deployments. Same EZ Cloud experience either way.',
  },
  {
    q: 'What about AX 2012?',
    a: 'Supported for customers mid-migration to F&O. Integration uses AIF services + direct API calls. We recommend using AX support as a bridge while planning F&O cutover.',
  },
  {
    q: 'Does EZ Cloud integrate with Power Platform?',
    a: "Yes. Post-invoice events (approved, posted, rejected, paid) can fire Power Automate triggers. Dataverse data can be referenced for routing decisions. If you've built operational automation on Power Platform, EZ Cloud fits in rather than around it.",
  },
  {
    q: 'How does the integration handle multi-legal-entity setups?',
    a: "EZ Cloud reads your full legal-entity structure from Dynamics and routes invoices to the correct LE during capture. Intercompany processing is standardized across entities. Approvers see only the LEs they're assigned to.",
  },
  {
    q: 'How long does implementation take?',
    a: 'Typical: 8-12 weeks (Business Central often toward the shorter end). Critical-path items are financial-dimension mapping, approval workflow setup, and security role alignment.',
  },
  {
    q: 'Is it on Microsoft AppSource?',
    a: 'Contact sales for current AppSource status. Deployment does not require marketplace listing — standard Dynamics APIs are used.',
  },
];

const Dynamics = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [roiInputs, setRoiInputs] = useState({ invoices: 3500, costPerInvoice: 18, staffRate: 36 });
  const annualSavings = useMemo(() => {
    const minutesSavedPerInvoice = 12;
    const hoursSaved = (roiInputs.invoices * minutesSavedPerInvoice) / 60;
    const laborMonthly = hoursSaved * roiInputs.staffRate;
    const processingMonthly = roiInputs.invoices * roiInputs.costPerInvoice * 0.5;
    return Math.max(0, (laborMonthly + processingMonthly) * 12);
  }, [roiInputs]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Microsoft Dynamics 365 AP Automation | F&O + BC — EZ Cloud"
        description="Native AP automation for Microsoft Dynamics 365 Finance & Operations and Business Central. Multi-legal-entity, financial-dimension coding, Power Platform-friendly."
        canonical="https://ezcloud.ai/integrations/dynamics"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
          { name: 'Microsoft Dynamics 365', url: 'https://ezcloud.ai/integrations/dynamics' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <section className="bg-hero-gradient text-white pt-16 pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="relativecontainer mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <Network className="w-4 h-4" aria-hidden />
                Integration · Microsoft Dynamics 365
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                AP automation for Dynamics 365 — F&O and Business Central.
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-8">
                Multi-legal-entity routing. Full financial-dimension coding. Power Platform–friendly. One integration, whether you're running F&O, Business Central, or migrating from AX.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" variant="accent" className="font-semibold" onClick={() => openCalendlyDemo()}>
                  <Calendar className="mr-2 w-5 h-5" aria-hidden />
                  See Dynamics integration live
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
                <Button size="lg" variant="outline-on-dark" asChild>
                  <Link to="/technology/native-erp">How our ERP integration works</Link>
                </Button>
              </div>
            </div>

            <Card className="p-6 bg-white/10 border-white/20 backdrop-blur-xl">
              <h2 className="text-white font-semibold mb-4">How the integration works</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-accent/20 rounded-lg border border-accent/30">
                  <div className="font-bold text-white">EZ Cloud</div>
                  <ArrowRight className="w-4 h-4 text-accent mx-auto" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Posts vendor invoices with dimensions, match results, images, tax</div>
                </div>
                <div className="text-center text-xs text-white/50 py-1">Dynamics REST / OData APIs · bi-directional</div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="font-bold text-white">Dynamics 365</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 mx-auto rotate-180" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Returns vendors, COA + dimensions, POs + receipts, legal entities</div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-4">
                F&O, Business Central, and AX 2012 supported with the same EZ Cloud UX.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">What flows between EZ Cloud and Dynamics</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">EZ Cloud → Dynamics</h3>
              </div>
              <ul className="space-y-3">
                {EZCLOUD_TO_DYNAMICS.map(({ label, note }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="text-gray-900 font-medium">{label}</div>
                      <div className="text-sm text-gray-500">{note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Dynamics → EZ Cloud</h3>
              </div>
              <ul className="space-y-3">
                {DYNAMICS_TO_EZCLOUD.map((label) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="text-gray-900 font-medium">{label}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">What you get from day one</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {DAY_ONE.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-gray-600">{body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Typical EZ Cloud + Dynamics customer</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'D365 Finance & Operations or Business Central',
              'Multi-legal-entity operations (F&O) or multi-company (BC)',
              '1,500 – 50,000 invoices per month',
              'Heavy financial-dimension usage for project / cost-center reporting',
              'Existing Power Platform / Power Automate investment',
              'Mid-migration from AX 2012',
            ].map((x) => (
              <div key={x} className="flex items-start gap-2 text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                <span>{x}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/industries/manufacturing" className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors">
              Manufacturing <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Rough Dynamics ROI</h2>
              <p className="text-gray-600 mb-6">Tune the inputs or jump to an industry calculator for a closer fit.</p>
              <Button variant="outline" asChild>
                <Link to="/resources/roi-calculator">Full ROI calculator</Link>
              </Button>
            </div>
            <Card className="p-6 bg-accent/10 border-accent/30">
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Monthly invoices</label>
                  <input type="number" min={0} value={roiInputs.invoices}
                    onChange={(e) => setRoiInputs({ ...roiInputs, invoices: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cost per invoice ($)</label>
                  <input type="number" min={0} value={roiInputs.costPerInvoice}
                    onChange={(e) => setRoiInputs({ ...roiInputs, costPerInvoice: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">AP staff rate ($/hr)</label>
                  <input type="number" min={0} value={roiInputs.staffRate}
                    onChange={(e) => setRoiInputs({ ...roiInputs, staffRate: Math.max(0, Number(e.target.value) || 0) })}
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900" />
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-700 mb-1">Estimated annual savings</div>
                <div className="text-4xl font-bold text-accent">${Math.round(annualSavings).toLocaleString()}</div>
              </div>
              <ROIResultsCapture
                industry="Dynamics 365 Integration"
                annualSavings={annualSavings}
                inputs={{
                  'Monthly invoices': roiInputs.invoices.toLocaleString(),
                  'Cost per invoice': `$${roiInputs.costPerInvoice}`,
                  'Staff hourly rate': `$${roiInputs.staffRate}`,
                }}
                buttonLabel="Email my Dynamics ROI report"
              />
            </Card>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Dynamics integration FAQ
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">See Dynamics + EZ Cloud in a live demo</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="accent" onClick={() => openCalendlyDemo()}>
              Book a Dynamics demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/technology/native-erp">
                <FileText className="mr-2 w-5 h-5" aria-hidden />
                How native integration works
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dynamics;
